import { memo, useCallback, useEffect, useMemo, useState } from "react";

import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import type { QuestionnairePayload } from "../../form-builder/types";
import { useQuestionnaireManagement } from "../../form-builder/hooks";
import {
  filterQuestionnaires,
  type QuestionnaireCategoryFilter,
  type QuestionnaireStatusFilter,
} from "../utils/questionnaireManagementFilterUtils";

import QuestionnairePreviewDialog from "./QuestionnairePreviewDialog";
import ConfirmDeleteQuestionnaireDialog from "./ConfirmDeleteQuestionnaireDialog";
import QuestionnaireManagementList from "./QuestionnaireManagementList";
import QuestionnaireManagementToolbar from "./QuestionnaireManagementToolbar";

export interface QuestionnaireManagementPanelSectionProps {
  organizationId: string;
  onEditQuestionnaire: (questionnaireId: string) => void;
  onCreateQuestionnaire: () => void;
}

const QuestionnaireManagementPanelSection = memo(
  ({
    organizationId,
    onEditQuestionnaire,
    onCreateQuestionnaire,
  }: QuestionnaireManagementPanelSectionProps) => {
    const {
      questionnaires,
      isLoading,
      errorMessage,
      deleteQuestionnaire,
      isDeleting,
      deleteErrorMessage,
    } = useQuestionnaireManagement({ organizationId });

    const [questionnaireForPreview, setQuestionnaireForPreview] =
      useState<QuestionnairePayload | null>(null);
    const [questionnaireIdPendingDelete, setQuestionnaireIdPendingDelete] =
      useState<string | null>(null);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] =
      useState<QuestionnaireStatusFilter>("all");
    const [categoryFilter, setCategoryFilter] =
      useState<QuestionnaireCategoryFilter>("all");

    const questionnairePendingDeleteTitle = useMemo(() => {
      if (!questionnaireIdPendingDelete) return undefined;
      return questionnaires.find((q) => q.id === questionnaireIdPendingDelete)
        ?.title;
    }, [questionnaires, questionnaireIdPendingDelete]);

    const filteredQuestionnaires = useMemo(
      () =>
        filterQuestionnaires(questionnaires, {
          searchQuery,
          statusFilter,
          categoryFilter,
        }),
      [categoryFilter, questionnaires, searchQuery, statusFilter],
    );

    const handleOpenPreview = useCallback(
      (questionnaire: QuestionnairePayload) => {
        setQuestionnaireForPreview(questionnaire);
      },
      [],
    );

    const handleClosePreview = useCallback(() => {
      setQuestionnaireForPreview(null);
    }, []);

    const handleOpenDelete = useCallback((questionnaireId: string) => {
      setQuestionnaireIdPendingDelete(questionnaireId);
    }, []);

    const handleCloseDelete = useCallback(() => {
      setQuestionnaireIdPendingDelete(null);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
      if (!questionnaireIdPendingDelete) return;
      await deleteQuestionnaire({
        questionnaireId: questionnaireIdPendingDelete,
      });
      setQuestionnaireIdPendingDelete(null);
    }, [deleteQuestionnaire, questionnaireIdPendingDelete]);

    // Mantém o primeiro item como selecionado quando a lista carregar.
    // Também evita que `activeId` aponte para um item inexistente (ex.: após deleção).
    useEffect(() => {
      if (questionnaires.length === 0) {
        if (activeId !== null) setActiveId(null);
        return;
      }

      const isStillPresent = activeId
        ? questionnaires.some((q) => q.id === activeId)
        : false;
      if (isStillPresent) return;

      setActiveId(questionnaires[0]?.id ?? null);
    }, [activeId, questionnaires]);

    return (
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant="h6" fontWeight={700}>
              Questionários
            </Typography>

            <QuestionnaireManagementToolbar
              searchQuery={searchQuery}
              statusFilter={statusFilter}
              categoryFilter={categoryFilter}
              onChangeSearchQuery={setSearchQuery}
              onChangeStatusFilter={setStatusFilter}
              onChangeCategoryFilter={setCategoryFilter}
              onCreateQuestionnaire={onCreateQuestionnaire}
            />

            <QuestionnaireManagementList
              questionnaires={filteredQuestionnaires}
              isLoading={isLoading}
              errorMessage={errorMessage}
              activeId={activeId}
              isDeleting={isDeleting}
              emptyMessage="Nenhum questionário encontrado com os filtros aplicados."
              onSelectQuestionnaire={setActiveId}
              onEditQuestionnaire={onEditQuestionnaire}
              onOpenPreview={handleOpenPreview}
              onOpenDelete={handleOpenDelete}
            />
          </Stack>
        </CardContent>

        {questionnaireForPreview && (
          <QuestionnairePreviewDialog
            open={Boolean(questionnaireForPreview)}
            questionnaire={questionnaireForPreview}
            onClose={handleClosePreview}
          />
        )}

        {questionnaireIdPendingDelete && (
          <ConfirmDeleteQuestionnaireDialog
            open={Boolean(questionnaireIdPendingDelete)}
            title={questionnairePendingDeleteTitle}
            isConfirming={isDeleting}
            errorMessage={deleteErrorMessage}
            onClose={handleCloseDelete}
            onConfirm={handleConfirmDelete}
          />
        )}
      </Card>
    );
  },
);

QuestionnaireManagementPanelSection.displayName =
  "QuestionnaireManagementPanelSection";

export default QuestionnaireManagementPanelSection;
